<script>
import { ref, watch } from 'vue';
import pdfMake from 'pdfmake/build/pdfmake';
import { pdfFileName, pdfDateRange, openPdf } from '@/utils/pdfFile';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';
import BalanceSheetServices from '@/services/BalanceSheetServices';
import companyLogo from '@/assets/al-madina-logo-base64';

export default {
  props: {
    salesman_id: Number,
    count_number: Number,
    start_date: [String, Date],
    end_date: [String, Date],
  },

  setup(props) {
    const documentDefinition = ref({});

    // 1,234,567.89 — thousands separators, up to 2 decimals.
    const fmt = (value) => {
      const n = Number(value || 0);
      return n.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    };
    // Raw number, no thousands separators (used inside the SL description,
    // matching the reference e.g. "Bill Amt: 196730").
    const raw = (value) => String(Number(value || 0));
    // Fixed 2-decimals (opening balance / report header).
    const fmt2 = (value) =>
      Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    // Debit balance shows "DB", credit (negative) shows "CR".
    const dbcr = (value) => (Number(value) < 0 ? 'CR' : 'DB');

    // Voucher type code, mirroring the reference (SL = sale, CV = cash/receipt,
    // RT = return).
    const voucherCode = (type) => {
      if (type === 'debit') return 'SL';
      if (type === 'credit') return 'CV';
      if (type === 'return') return 'RT';
      return '';
    };

    const loadPdf = async () => {
      const response = await BalanceSheetServices.getSalesmanLedgerPdfData(
        props.salesman_id,
        moment(props.start_date).format('YYYY-MM-DD'),
        moment(props.end_date).format('YYYY-MM-DD')
      );

      const rows = response.data.rows || [];
      const opening = Number(response.data.opening_balance || 0);
      const sm = response.data.salesman || {};
      const salesmanName = `${sm.first_name ?? ''} ${sm.last_name ?? ''}`.trim();

      // ---- Build table body ----
      const body = [];

      // Column header (repeats on every page via headerRows).
      body.push([
        { text: 'Voucher#', style: 'th' },
        { text: 'Date', style: 'th', alignment: 'center' },
        { text: 'Description', style: 'th' },
        { text: 'Debit', style: 'th', alignment: 'right' },
        { text: 'Credit', style: 'th', alignment: 'right' },
        { text: 'Total', style: 'th', alignment: 'right' },
      ]);

      let running = opening;
      let totalDebit = 0;
      let totalCredit = 0;
      let totalDiscount = 0;
      let totalFreight = 0;
      let lastShopId = null;
      // Body indexes of bill detail sub-rows; no rule is drawn above them so
      // they read as part of the bill line.
      const subRows = new Set();

      rows.forEach((row) => {
        // Bold shop/account header above each group (in the Description column),
        // matching the reference layout.
        if (row.shop_id !== lastShopId) {
          lastShopId = row.shop_id;
          body.push([
            { text: '' },
            {
              text: `${row.shop_id}`,
              style: 'groupHeader',
              alignment: 'center',
              bold: true,
              fontSize: 11,
              color: '#777',
            },
            {
              colSpan: 4,
              style: 'groupHeader',
              text: [
                { text: row.shop_name || '', bold: true, fontSize: 13, color: '#000' },
                {
                  text: row.shop_address ? `  -  ${row.shop_address}` : '',
                  bold: true,
                  fontSize: 11,
                  color: '#000',
                },
              ],
            },
            {}, {}, {},
          ]);
        }

        const amt = Number(row.amount || 0);
        const cart = Number(row.freight || 0); // freight = "Cart" in the reference
        const disc = Number(row.bill_discount || 0);

        let debit = 0;
        let credit = 0;
        if (row.type === 'debit') {
          // Debit = stored receivable (gross - discount) minus freight, the same
          // as the bill and the shopkeeper ledger. The opening balance from the
          // backend is net of freight on earlier bills too.
          debit = amt - cart;
          totalDiscount += disc;
          totalFreight += cart;
        } else if (row.type === 'return') {
          // Returns are stored as negative amounts (balance += amount), so
          // negate to show a positive credit that reduces the receivable.
          credit = -amt;
        } else {
          credit = amt;
        }

        running = running + debit - credit;
        totalDebit += debit;
        totalCredit += credit;

        const voucherNo =
          row.type === 'return'
            ? row.return_id || row.id
            : row.billing_id || row.id;

        let descText;
        let billDetail = null;
        if (row.type === 'debit') {
          // SL line shows the description; underneath, on its own row:
          // (+)freight: <-freight>, (-)Disc: <discount>, Bill Amt: <gross>
          descText = row.description || 'Sale Bill';
          const parts = [];
          if (cart) parts.push(`(+)freight: ${raw(-cart)}`);
          if (disc) parts.push(`(-)Disc: ${raw(disc)}`);
          parts.push(`Bill Amt: ${raw(amt + disc)}`); // gross = stored amount + discount
          billDetail = parts.join(',   ');
        } else {
          descText = row.description ?? '';
          if (!descText) {
            if (row.type === 'credit') descText = 'Amount Received';
            else if (row.type === 'return') descText = 'Return';
            else descText = 'Sale Bill';
          }
        }

        body.push([
          { text: `${voucherCode(row.type)} ${voucherNo}`, style: 'cell' },
          {
            text: moment(row.payment_at || row.created_at).format('DD-MM-YY'),
            style: 'cell',
            alignment: 'center',
          },
          { text: descText, style: 'cell' },
          {
            text: debit ? fmt(debit) : '-',
            style: 'cell',
            alignment: 'right',
          },
          {
            text: credit ? fmt(credit) : '-',
            style: 'cell',
            alignment: 'right',
          },
          {
            text: `${fmt(running)} ${dbcr(running)}`,
            style: 'cell',
            alignment: 'right',
          },
        ]);
        if (billDetail) {
          subRows.add(body.length);
          body.push([
            { text: '' },
            { text: '' },
            { text: billDetail, style: 'subCell', colSpan: 4 },
            {}, {}, {},
          ]);
        }
      });

      // End-of-report totals are rendered as a separate boxed table (below),
      // so the three figures sit inside borders like the reference.

      const fromTxt = moment(props.start_date).format('DD-MMM-YYYY');
      const toTxt = moment(props.end_date).format('DD-MMM-YYYY');

      // Repeating page header band (matches the reference statement).
      const pageHeader = (currentPage, pageCount) => ({
        margin: [40, 18, 40, 0],
        stack: [
          {
            columns: [
              {
                width: '*',
                stack: [
                  { text: `Print Date : ${moment().format('DD-MMM-YYYY')}`, style: 'hdrSmall' },
                  { text: `Time........ : ${moment().format('hh:mm:ss a')}`, style: 'hdrSmall' },
                ],
              },
              {
                width: 160,
                image: companyLogo,
                fit: [150, 80],
                alignment: 'center',
              },
              {
                width: '*',
                alignment: 'right',
                stack: [
                  { text: `Page # : ${currentPage} / ${pageCount}`, style: 'hdrSmall' },
                  { text: 'User..... : ', style: 'hdrSmall' },
                ],
              },
            ],
          },
          {
            margin: [0, 8, 0, 0],
            columns: [
              {
                width: '*',
                text: [
                  { text: 'Code : ', style: 'hdrLabel' },
                  { text: `${props.salesman_id}      `, style: 'hdrValue' },
                  { text: 'Account Title : ', style: 'hdrLabel' },
                  { text: salesmanName, style: 'hdrTitle' },
                ],
              },
            ],
          },
          {
            margin: [0, 3, 0, 0],
            text: [
              { text: 'Report From : ', style: 'hdrLabel' },
              { text: `${fromTxt}   `, style: 'hdrValue' },
              { text: 'To  ', style: 'hdrLabel' },
              { text: toTxt, style: 'hdrValue' },
            ],
          },
          {
            margin: [0, 3, 0, 4],
            columns: [
              {
                width: '*',
                text: [
                  { text: 'Group / LOT : ', style: 'hdrLabel' },
                  { text: 'ALL', style: 'hdrValue' },
                ],
              },
              {
                width: 'auto',
                alignment: 'right',
                text: [
                  { text: 'Opening Balance : ', style: 'hdrLabel' },
                  { text: `${fmt2(opening)} ${dbcr(opening)}`, style: 'hdrTitle' },
                ],
              },
            ],
          },
          {
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.8, lineColor: '#333' },
            ],
          },
        ],
      });

      const dd = {
        pageSize: 'A4',
        pageMargins: [40, 176, 40, 30],
        header: pageHeader,
        content: [
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: [58, 44, '*', 60, 60, 82],
              body: body,
            },
            layout: {
              // Borderless: only thin horizontal rules under the header, between
              // rows and above the totals. No vertical/box borders.
              hLineWidth: (i, node) => {
                if (i === 1 || i === node.table.body.length) return 0.6;
                return subRows.has(i) ? 0 : 0.3;
              },
              hLineColor: () => '#CCC',
              vLineWidth: () => 0,
              paddingTop: () => 3,
              paddingBottom: () => 3,
              paddingLeft: () => 2,
              paddingRight: () => 2,
            },
          },
          // End-of-report totals, with the three figures inside boxes.
          {
            margin: [0, 2, 0, 0],
            table: {
              widths: ['*', 60, 60, 82],
              body: [
                [
                  {
                    text: 'End of Report Total >>>',
                    alignment: 'right',
                    style: 'totalLabel',
                    border: [false, false, false, false],
                  },
                  {
                    text: fmt(totalDebit),
                    style: 'totalCell',
                    alignment: 'right',
                    border: [true, true, true, true],
                  },
                  {
                    text: fmt(totalCredit),
                    style: 'totalCell',
                    alignment: 'right',
                    border: [true, true, true, true],
                  },
                  {
                    text: `${fmt(running)} ${dbcr(running)}`,
                    style: 'totalCell',
                    alignment: 'right',
                    border: [true, true, true, true],
                  },
                ],
                [
                  {
                    text: 'Total Discount >>>',
                    alignment: 'right',
                    style: 'totalLabel',
                    border: [false, false, false, false],
                  },
                  {
                    text: fmt(totalDiscount),
                    style: 'totalCell',
                    alignment: 'right',
                    border: [true, true, true, true],
                  },
                  { text: '', colSpan: 2, border: [false, false, false, false] },
                  {},
                ],
                [
                  {
                    text: 'Total Freight >>>',
                    alignment: 'right',
                    style: 'totalLabel',
                    border: [false, false, false, false],
                  },
                  {
                    text: fmt(totalFreight),
                    style: 'totalCell',
                    alignment: 'right',
                    border: [true, true, true, true],
                  },
                  { text: '', colSpan: 2, border: [false, false, false, false] },
                  {},
                ],
              ],
            },
            layout: {
              hLineColor: () => '#333',
              vLineColor: () => '#333',
              paddingLeft: () => 3,
              paddingRight: () => 3,
              paddingTop: () => 3,
              paddingBottom: () => 3,
            },
          },
        ],
        styles: {
          th: { bold: true, fontSize: 10, fillColor: '#EDEDED', margin: [0, 2, 0, 2] },
          cell: { fontSize: 10, margin: [0, 1, 0, 1] },
          subCell: { fontSize: 9, color: '#555', margin: [0, -2, 0, 1] },
          groupHeader: { fontSize: 10, margin: [0, 3, 0, 1] },
          totalLabel: { bold: true, fontSize: 9, margin: [0, 3, 4, 3] },
          totalCell: { bold: true, fontSize: 9, margin: [0, 3, 0, 3] },
          hdrSmall: { fontSize: 8 },
          hdrLabel: { fontSize: 9 },
          hdrValue: { fontSize: 9, bold: true },
          hdrTitle: { fontSize: 11, bold: true, decoration: 'underline' },
        },
      };

      documentDefinition.value = dd;
      pdfMake.vfs = pdfFonts;
      openPdf(pdfMake, dd, pdfFileName(['Salesman-Ledger', salesmanName], pdfDateRange(props.start_date, props.end_date)));
    };

    watch(
      () => props.count_number,
      (currentValue, oldValue) => {
        if (currentValue !== oldValue && currentValue !== '' && props.salesman_id) {
          loadPdf();
        }
      }
    );

    return { documentDefinition };
  },
};
</script>
